/** Full-page, read-only viewer for a COMMITTED pull plan. Replaces the app
 *  chrome (overlay above the top nav) like opening the board does, with a slim
 *  top bar + a Back arrow and three tabs that each fill the page: the real Takt
 *  board, the real dependency Gantt, and a review-style "what changed" table —
 *  all rendered from the plan's frozen snapshot. */
import React from "react";
import { fetchPullPlanSnapshot } from "../../../api/clientV2";
import type { PullPlanSnapshot, PullPlanSummary } from "../../../models";
import Spinner from "../../../components/Spinner/Spinner";
import { apiErrorMessage } from "../../../utils/apiError";
import { formatDate } from "../../../utils/format";
import BoardTab from "./BoardTab/BoardTab";
import ChangesTab from "./ChangesTab/ChangesTab";
import DependenciesTab from "./DependenciesTab/DependenciesTab";
import "./CompletedPlanView.css";

type TabId = "board" | "deps" | "changes";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "board", label: "Takt board" },
  { id: "deps", label: "Dependencies" },
  { id: "changes", label: "What changed" },
];

interface Props {
  scheduleId: number;
  plan: PullPlanSummary;
  sectionLabel: string;
  onClose: () => void;
}

const CompletedPlanView: React.FC<Props> = ({
  scheduleId,
  plan,
  sectionLabel,
  onClose,
}) => {
  const [snap, setSnap] = React.useState<PullPlanSnapshot | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<TabId>("board");

  React.useEffect(() => {
    let cancelled = false;
    setSnap(null);
    setError(null);
    fetchPullPlanSnapshot(scheduleId, plan.id)
      .then((s) => {
        if (!cancelled) setSnap(s);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(apiErrorMessage(e, "Failed to load this plan."));
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId, plan.id]);

  return (
    <div className="cpv-page">
      <div className="pull-plan-topbar cpv-topbar">
        <button type="button" className="cpv-back" onClick={onClose}>
          ← Plans
        </button>
        <span className="pull-plan-topbar-div" aria-hidden="true" />
        <h2 className="cpv-title">{plan.name}</h2>
        <span className="cpv-meta">
          {snap?.meta.takt_section_name || sectionLabel}
          {plan.committed_at && ` · Committed ${formatDate(plan.committed_at)}`}
        </span>
      </div>

      <nav className="cpv-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={"cpv-tab" + (tab === t.id ? " cpv-tab--active" : "")}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {snap?.meta.empty_scope ? (
        <div className="cpv-banner">
          ⚠ This plan's section or activities no longer exist in the current
          schedule, so its board can't be reconstructed.
        </div>
      ) : (
        snap?.meta.stale && (
          <div className="cpv-banner">
            ⚠ Approximate — committed before snapshots existed, so this reflects the
            current schedule and may have changed since.
          </div>
        )
      )}

      {error && <div className="pull-plan-error cpv-error">{error}</div>}

      <div className="cpv-body">
        <Spinner visible={!snap && !error} label="Loading plan…" />
        {snap && tab === "board" && <BoardTab snapshot={snap} />}
        {snap && tab === "deps" && <DependenciesTab snapshot={snap} />}
        {snap && tab === "changes" && <ChangesTab snapshot={snap} />}
      </div>
    </div>
  );
};

export default CompletedPlanView;
